import * as React from "react";
import { TextField, Box, styled, Button as MuiButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useFilterStyles } from "../../styles/filterStyles";

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF99',
    borderRadius: '12px',
    height: '40px',
    fontSize: '13px',
    fontFamily: 'inherit',
    '& fieldset': {
      border: '1.5px solid #E5E7EB',
    },
    '&:hover fieldset': {
      border: '1.5px solid #E5E7EB',
    },
    '&.Mui-focused fieldset': {
      border: '2px solid #3B82F6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    '& input': {
      padding: '8px 8px',
      paddingRight: 4,
      fontSize: '13px',
    },
    '& .MuiIconButton-root': {
      padding: 0,
      marginRight: 0,
      borderRadius: 0,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    '&.Mui-disabled': {
      '& fieldset': {
        border: '1.5px solid #E5E7EB',
      },
    },
  },
});

const StyledPresetButton = styled(MuiButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(({ active }) => ({
  backgroundColor: '#FFFFFF',
  border: active ? '1px solid #09CCA9' : '1.5px solid #E5E7EB',
  borderRadius: '8px',
  color: '#0F172A',
  fontSize: '11px',
  fontWeight: 500,
  padding: '4px 10px',
  textTransform: 'none',
  height: '28px',
  lineHeight: 1.2,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    border: active ? '1px solid #09CCA9' : '1.5px solid #E5E7EB',
  },
}));

// Common popper styles for both date pickers
const commonPopperStyles = {
  placement: 'bottom-start' as const,
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 4],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport' as const,
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
      },
    },
  ],
  sx: {
    '& .MuiPaper-root': {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: '12px',
      overflow: 'hidden',
      maxWidth: '260px', // Increased from 225px to accommodate header
    },
    '& .MuiDateCalendar-root': {
      height: 'auto',
      maxHeight: 'none',
      width: '260px', // Match the paper width
    },
    '& .MuiPickersSlideTransition-root': {
      minHeight: '180px',
    },
    '& .MuiPickersCalendarHeader-root': {
      padding: '4px 12px 2px 12px', // Increased horizontal padding
      minHeight: '28px',
      marginTop: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px', // Add gap between label and arrows
      // Month/year label first...
      '& .MuiPickersCalendarHeader-labelContainer': {
        order: 1,
        flex: '0 0 auto', // Don't shrink
        minWidth: 'fit-content',
      },
      '& .MuiPickersCalendarHeader-label': {
        fontSize: '11px',
        fontWeight: 600,
      },
      '& .MuiPickersArrowSwitcher-root': {
        order: 2,
        marginLeft: 'auto',
        marginRight: 0,
        display: 'flex',
        gap: '2px', // Reduced gap between arrows
        flexShrink: 0, // Prevent shrinking
      },
      '& .MuiPickersArrowSwitcher-root button': {
        padding: '4px',
        minWidth: '24px',
        width: '24px',
        margin: 0,
      },
      '& .MuiPickersArrowSwitcher-root .MuiSvgIcon-root': {
        fontSize: 16,
      },
    },
    '& .MuiDayCalendar-header': {
      paddingLeft: '12px', // Match header padding
      paddingRight: '12px',
      paddingTop: '2px',
      paddingBottom: '2px',
      justifyContent: 'flex-start',
      '& .MuiDayCalendar-weekDayLabel': {
        fontSize: '10px',
        fontWeight: 600,
        width: '30px', // Slightly wider to match new card width
        height: '20px',
        margin: '0 2px',
      },
    },
    '& .MuiDayCalendar-monthContainer': {
      paddingLeft: '12px', // Match header padding
      paddingRight: '12px',
      paddingTop: '2px',
      paddingBottom: '4px',
    },
    '& .MuiDayCalendar-weekContainer': {
      justifyContent: 'flex-start',
      margin: '0',
    },
    '& .MuiPickersDay-root': {
      fontSize: '11px',
      width: '30px', // Slightly wider to match new card width
      height: '28px',
      margin: '0 2px',
      '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
      },
      '&.Mui-selected': {
        backgroundColor: '#3B82F6',
        '&:hover': {
          backgroundColor: '#2563EB',
        },
      },
    },
    '& .MuiPickersCalendarHeader-switchViewButton': {
      padding: '2px',
      '& .MuiSvgIcon-root': {
        fontSize: '16px',
      },
    },
    '& .MuiYearCalendar-root': {
      width: '260px', // Match new width
      '& .MuiPickersYear-yearButton': {
        fontSize: '11px',
        height: '28px',
        margin: '2px 0',
      },
    },
    '& .MuiMonthCalendar-root': {
      width: '260px', // Match new width
      '& .MuiPickersMonth-monthButton': {
        fontSize: '11px',
        height: '28px',
        margin: '2px',
      },
    },
  },
};

interface DateRangeFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  onChangeStart: (value: Date | null) => void;
  onChangeEnd: (value: Date | null) => void;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
}) => {
  const filterStyles = useFilterStyles();
  const [activePresetDays, setActivePresetDays] = React.useState<number | null>(null);
  const [startDateError, setStartDateError] = React.useState<string | null>(null);
  const [endDateError, setEndDateError] = React.useState<string | null>(null);

  // Validate date range relationship when both dates are present and valid
  // Also clear errors when dates are cleared
  React.useEffect(() => {
    // Clear errors if either date is null
    if (!startDate || !endDate) {
      setStartDateError(null);
      setEndDateError(null);
      return;
    }

    // Validate both dates are valid Date objects
    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      if (startDate > endDate) {
        setStartDateError('Start date must be before end date');
        setEndDateError('End date must be after start date');
      } else {
        // Clear errors if dates are valid and in correct order
        setStartDateError(null);
        setEndDateError(null);
      }
    }
  }, [startDate, endDate]);

  React.useEffect(() => {
    if (!startDate || !endDate) {
      setActivePresetDays(null);
      return;
    }

    const toISODate = (d: Date) => d.toISOString().split('T')[0];
    const today = new Date();
    const todayISO = toISODate(today);

    const startISO = toISODate(startDate);
    const endISO = toISODate(endDate);

    if (endISO !== todayISO) {
      setActivePresetDays(null);
      return;
    }

    const daysFrom = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return toISODate(d);
    };

    if (startISO === daysFrom(7)) {
      setActivePresetDays(7);
      return;
    }
    if (startISO === daysFrom(30)) {
      setActivePresetDays(30);
      return;
    }

    setActivePresetDays(null);
  }, [startDate, endDate]);

  const handlePreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    onChangeStart(start);
    onChangeEnd(end);
    setActivePresetDays(days);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <Box className={filterStyles.dateRangeContainer} sx={{ flexWrap: 'nowrap', width: '100%', alignItems: 'flex-start' }}>
          <DatePicker
            enableAccessibleFieldDOMStructure={false}
            sx={{
              '& .MuiInputBase-root': {
                height: 40,
              },
              '& .MuiSvgIcon-root': {
                fontSize: 16,
              },
              '& .MuiInputAdornment-root': {
                marginRight: 0,
              },
            }}
            value={startDate}
            onChange={(date) => {
              setStartDateError(null);
              if (date) {
                // Validate date is valid
                if (isNaN(date.getTime())) {
                  setStartDateError('Invalid date');
                  return;
                }
                onChangeStart(date);
                // Validate start date is not greater than end date
                if (endDate && date > endDate) {
                  setStartDateError('Start date must be before end date');
                  setEndDateError('End date must be after start date');
                  // Don't automatically adjust end date - just show the error
                } else {
                  setEndDateError(null);
                }
                setActivePresetDays(null);
              } else {
                onChangeStart(null);
                setActivePresetDays(null);
              }
            }}
            onError={(error) => {
              if (error) {
                setStartDateError('Invalid date format');
              } else {
                setStartDateError(null);
              }
            }}
            format="MM/dd/yyyy"
            slots={{
              textField: StyledTextField,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                error: !!startDateError,
                helperText: startDateError,
                InputLabelProps: {
                  shrink: true,
                },
                sx: {
                  flex: 1,
                  minWidth: '120px',
                  '& .MuiOutlinedInput-root': {
                    width: '100%',
                  },
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                    lineHeight: '1.2',
                  },
                },
                FormHelperTextProps: {
                  sx: {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                },
              },
              popper: commonPopperStyles,
            }}
          />

          <span className={filterStyles.dateRangeSeparator} style={{ marginTop: '12px' }}>to</span>

          <DatePicker
            enableAccessibleFieldDOMStructure={false}
            sx={{
              '& .MuiInputBase-root': {
                height: 40,
              },
              '& .MuiSvgIcon-root': {
                fontSize: 16,
              },
              '& .MuiInputAdornment-root': {
                marginRight: 0,
              },
            }}
            value={endDate}
            onChange={(date) => {
              setEndDateError(null);
              if (date) {
                // Validate date is valid
                if (isNaN(date.getTime())) {
                  setEndDateError('Invalid date');
                  return;
                }
                onChangeEnd(date);
                // Validate end date is not less than start date
                if (startDate && date < startDate) {
                  setEndDateError('End date must be after start date');
                  setStartDateError('Start date must be before end date');
                  // Don't automatically adjust start date - just show the error
                } else {
                  setStartDateError(null);
                }
                setActivePresetDays(null);
              } else {
                onChangeEnd(null);
                setActivePresetDays(null);
              }
            }}
            onError={(error) => {
              if (error) {
                setEndDateError('Invalid date format');
              } else {
                setEndDateError(null);
              }
            }}
            format="MM/dd/yyyy"
            disabled={!startDate}
            slots={{
              textField: StyledTextField,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                error: !!endDateError,
                helperText: endDateError,
                InputLabelProps: {
                  shrink: true,
                },
                sx: {
                  flex: 1,
                  minWidth: '120px',
                  '& .MuiOutlinedInput-root': {
                    width: '100%',
                    backgroundColor: startDate ? '#FFFFFF99' : '#F9FAFB',
                  },
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                    lineHeight: '1.2',
                  },
                },
                FormHelperTextProps: {
                  sx: {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                },
              },
              popper: commonPopperStyles,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: '8px' }}>
          <StyledPresetButton active={activePresetDays === 7} onClick={() => handlePreset(7)}>
            Last 7 days
          </StyledPresetButton>
          <StyledPresetButton active={activePresetDays === 30} onClick={() => handlePreset(30)}>
            Last 30 days
          </StyledPresetButton>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;